import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  X,
  Bell,
} from 'lucide-react';
import type { NotificationTypeValue } from '@sime/shared';
import { useUnreadCount } from '@/api/notifications';
import { cn } from '@/lib/utils';

// --- Types ---

interface ToastItem {
  id: string;
  type: NotificationTypeValue;
  title: string;
  message: string;
  createdAt: string;
}

// --- Icon/color mapping ---

const TOAST_ICON_MAP: Record<string, typeof CheckCircle2> = {
  TICKET_APPROVED: CheckCircle2,
  TICKET_REJECTED: XCircle,
  EXCEL_UPLOAD_COMPLETE: Info,
  EXCEL_UPLOAD_FAILED: XCircle,
  CLOSURE_COMPLETE: CheckCircle2,
  PACKAGE_READY: CheckCircle2,
  LOTE_SENT: Info,
  REMINDER_FIRMA: AlertTriangle,
  TICKET_ASSIGNED: Info,
  TICKET_TRANSITIONED: Info,
  APPROVAL_REQUESTED: Bell,
  BATCH_READY: Bell,
  SYSTEM_ALERT: AlertTriangle,
};

const TOAST_COLOR_MAP: Record<string, string> = {
  TICKET_APPROVED: 'var(--sime-success)',
  TICKET_REJECTED: 'var(--sime-danger)',
  EXCEL_UPLOAD_COMPLETE: 'var(--sime-accent)',
  EXCEL_UPLOAD_FAILED: 'var(--sime-danger)',
  CLOSURE_COMPLETE: 'var(--sime-success)',
  PACKAGE_READY: 'var(--sime-success)',
  LOTE_SENT: 'var(--sime-accent)',
  REMINDER_FIRMA: 'var(--sime-warning)',
  TICKET_ASSIGNED: 'var(--sime-accent)',
  TICKET_TRANSITIONED: 'var(--sime-accent)',
  APPROVAL_REQUESTED: 'var(--sime-warning)',
  BATCH_READY: 'var(--sime-accent)',
  SYSTEM_ALERT: 'var(--sime-warning)',
};

const NOTIFICATION_ROUTE_MAP: Partial<Record<NotificationTypeValue, string>> = {
  TICKET_APPROVED: '/tickets',
  TICKET_REJECTED: '/tickets',
  TICKET_ASSIGNED: '/tickets',
  TICKET_TRANSITIONED: '/tickets',
  EXCEL_UPLOAD_COMPLETE: '/excel-ingestion',
  EXCEL_UPLOAD_FAILED: '/excel-ingestion',
  CLOSURE_COMPLETE: '/cierre',
  PACKAGE_READY: '/cierre/paquete',
  LOTE_SENT: '/tickets',
  APPROVAL_REQUESTED: '/tickets',
  BATCH_READY: '/tickets',
};

// --- Single Toast ---

function Toast({
  toast,
  onDismiss,
  onNavigate,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  onNavigate: (type: NotificationTypeValue) => void;
}) {
  const Icon = TOAST_ICON_MAP[toast.type] ?? Info;
  const color = TOAST_COLOR_MAP[toast.type] ?? 'var(--sime-accent)';
  const hasRoute = toast.type in NOTIFICATION_ROUTE_MAP;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = requestAnimationFrame(() => { setVisible(true); });
    return () => { cancelAnimationFrame(enterTimer); };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, 5000);
    return () => { clearTimeout(timer); };
  }, [toast.id, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'w-full md:w-80 rounded-2xl p-4 bg-sime-surface',
        'shadow-neu-raised',
        'border border-sime-border-subtle',
        'transition-all duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]',
        hasRoute && 'cursor-pointer',
        visible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-10 scale-95',
      )}
      onClick={hasRoute ? () => { onNavigate(toast.type); } : undefined}
    >
      <div className="flex items-start gap-3">
        <div
          className="p-1.5 rounded-lg shrink-0"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="h-4 w-4" style={{ color }} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-sime-text-primary truncate">
            {toast.title}
          </p>
          <p className="text-[12px] text-sime-text-secondary mt-0.5 line-clamp-2">
            {toast.message}
          </p>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
          }}
          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-sime-text-tertiary hover:text-sime-text-primary transition-colors shrink-0"
          aria-label="Cerrar notificacion"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// --- Toast Container (Provider) ---

export function NotificationToastContainer() {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const prevCountRef = useRef<number | null>(null);

  const { data: unreadData } = useUnreadCount();
  const currentCount = unreadData?.data.count ?? 0;

  // Check for new notifications by comparing unread count
  useEffect(() => {
    if (prevCountRef.current === null) {
      // First load, just record the count, don't show toast
      prevCountRef.current = currentCount;
      return;
    }

    if (currentCount > prevCountRef.current) {
      const newCount = currentCount - prevCountRef.current;
      const newToast: ToastItem = {
        id: `toast-${Date.now()}`,
        type: 'SYSTEM_ALERT',
        title: newCount === 1 ? 'Nueva notificacion' : `${newCount} nuevas notificaciones`,
        message: newCount === 1
          ? 'Tienes una nueva notificacion sin leer.'
          : `Tienes ${newCount} notificaciones nuevas sin leer.`,
        createdAt: new Date().toISOString(),
      };

      setToasts((prev) => {
        // Max 3 visible
        const updated = [newToast, ...prev].slice(0, 3);
        return updated;
      });
    }

    prevCountRef.current = currentCount;
  }, [currentCount]);

  const handleDismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleNavigate = useCallback((type: NotificationTypeValue) => {
    const route = NOTIFICATION_ROUTE_MAP[type];
    if (route) {
      void navigate(route);
    }
  }, [navigate]);

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:bottom-auto md:left-auto md:top-20 md:right-6 z-sime-toast flex flex-col gap-3"
      aria-label="Notificaciones"
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onDismiss={handleDismiss}
          onNavigate={handleNavigate}
        />
      ))}
    </div>
  );
}
